/* eslint-disable no-console */
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Checkbox, Divider, Drawer, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { Controller, useForm } from "react-hook-form";
import CloseIcon from "../../../Common/Svgs/CloseIcon";
import CustomInput from "../../../Core/Components/CustomInput";
import Button from "../../../Core/Components/CustomButton";
import { updateBatteries } from "../../../Redux/Slices/batteries.slice";
import AlertIcon from "../../../Common/Svgs/AlertIcon";
import { client } from "../../../Utils/axiosClient";
import { getBatteryV3 } from "../../../Redux/APIs/batteriesAPI";
import BatteryPercentage from "../../../Core/Components/BatteryPercentage";
import { PopoverDropdown } from "../../../Core/Components/CustomDropdown";
// import { getHubAgents } from "../../../Redux/APIs/trikeBreakdownAPI";

const AcceptBatteryRequestModal = ({ visible, onCancel }) => {
  const dispatch = useDispatch();

  const { acceptBatteryRequestModal } = useSelector((state) => state.batteries);

  const [batteries, setBatteries] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [focus, setFocus] = useState(false);
  const [selected, setSelected] = useState([]);
  const [disabled, setDisabled] = useState("");

  const { control, formState, setValue, clearErrors } = useForm({
    defaultValues: {
      hubId: acceptBatteryRequestModal?.data?.hubId || null,
      hubName: acceptBatteryRequestModal?.data?.hubName || "",
      agentId: selectedAgent?.id || null,
      agentName: selectedAgent?.value || "",
    },
  });

  const getFilteredBatteries = useCallback(() => {
    return batteries.filter((item) => item.registrationNo.includes(search));
  }, [batteries, search]);

  const lowChargeCount = getFilteredBatteries()
    .filter((battery) => selected.includes(battery.id))
    .filter((battery) => Number(battery.percentage) < 50).length;

  const hasLowChargeBattery = lowChargeCount > 0;

  const getBatteries = useCallback(async () => {
    if (visible) {
      try {
        setLoading(true);
        const response = await client.get(
          `${process.env.REACT_APP_API_URL}/battery/get-batteries-for-assigning`,
          {
            params: {
              supplierHubId: acceptBatteryRequestModal?.data?.supplierHubId,
            },
          },
        );

        console.log(response.data);
        setBatteries([...response.data.batteries]);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const getAgents = useCallback(async () => {
    if (visible && acceptBatteryRequestModal?.data?.supplierHubId) {
      try {
        const response = await client.get(
          `${process.env.REACT_APP_API_URL}/hub-station/hub-agents`,
          {
            params: {
              hubId: acceptBatteryRequestModal?.data?.supplierHubId,
            },
          },
        );
        setAgents(
          response.data.map((agent) => {
            return { id: agent.id, value: agent.name };
          }) || [],
        );
      } catch (error) {
        console.log("Failed to fetch hub agents:", error);
      }
    }
  }, [visible, acceptBatteryRequestModal?.data?.supplierHubId]);

  const handleSelection = (id) => {
    if (selected.includes(id)) {
      const local = selected.filter((item) => item !== id);
      setSelected(local);
    } else {
      setSelected((current) => [...current, id]);
    }
  };

  const handleCancel = () => {
    setSelected([]);
    setSelectedAgent("");
    setSearch("");
    setSelectedAgent();
    onCancel();
  };

  const handleConfirm = async () => {
    if (!selectedAgent) {
      setDisabled("Please assign a withdrawal hub agent.");
      return;
    }

    if (selected.length > acceptBatteryRequestModal?.data?.batteries) {
      setDisabled("Selected batteries exceed the requested count.");
      return;
    }

    if (selected.length < acceptBatteryRequestModal?.data?.batteries) {
      setDisabled("Less no. of batteries selected.");
      return;
    }

    try {
      setDisabled("");
      dispatch(updateBatteries({ key: "loading", value: true }));

      const payload = {
        batteries: selected,
        requestId: parseInt(acceptBatteryRequestModal?.data?.requestId, 10),
        agentId: parseInt(selectedAgent?.id, 10),
      };

      const response = await client.post(
        `${process.env.REACT_APP_API_URL}/battery/assign-batteries`,
        payload,
      );

      if (response.status === 200) {
        dispatch(
          updateBatteries({
            key: "showToastMessage",
            value: {
              visible: true,
              data: {
                title: "Battery request accepted.",
                description:
                  "Please proceed to checkout the batteries in the next 1 hour to initiate this transfer.",
              },
            },
          }),
        );

        await dispatch(getBatteryV3());
      }
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(updateBatteries({ key: "loading", value: false }));
      setDisabled("");
      setSelected([]);
      setSelectedAgent("");
      onCancel();
    }
  };

  useEffect(() => {
    getBatteries();
  }, [getBatteries]);

  useEffect(() => {
    getAgents();
  }, [getAgents]);

  const requestedBatteryCount = acceptBatteryRequestModal?.data?.batteries;

  return (
    <Drawer
      width={560}
      placement="right"
      closable={false}
      open={visible}
      onClose={onCancel}
      maskStyle={{
        background: "transparent",
        boxShadow: "12px 12px 16px -4px #10182814",
      }}
    >
      <div className="rounded-xl bg-background-white w-full h-full relative flex flex-col ">
        <div className="flex items-center justify-between pl-4 pr-6 pt-4">
          <div className="flex items-center gap-x-3">
            <h1 className="font-poppins text-base font-medium">
              Request details
            </h1>
          </div>

          <button type="button" onClick={handleCancel}>
            <CloseIcon className="w-6 h-6 stroke-dark-gray" />
          </button>
        </div>

        <div className="mx-6 mt-6">
          <Divider className="my-0" />
        </div>

        <div className="mx-6 pt-6">
          <p className="font-poppins font-semibold text-2xl text-black">
            {requestedBatteryCount || "0"}
          </p>
          <p className="mt-1 font-aileron font-normal text-xs text-dark-gray">
            No. of Batteries Requested
          </p>
        </div>

        <div className="px-6 mt-6">
          <div className="flex justify-between">
            <div>
              <p className="font-aileron font-normal text-xs leading-none text-dark-gray mb-2">
                Hub Station
              </p>

              <div
                role="button"
                className="px-3.5 py-2 w-[248px] flex items-center justify-between border rounded-md bg-background cursor-not-allowed"
              >
                <p className="font-aileron text-xs capitalize text-dark-gray">
                  {acceptBatteryRequestModal?.data?.supplierHubName || ""}
                </p>
              </div>
            </div>

            <div>
              <p className="font-aileron font-normal text-xs leading-none text-dark-gray mb-2">
                Technical Agent
              </p>

              <Controller
                name="agentName"
                control={control}
                rules={{
                  required: "Required.",
                }}
                render={({ field }) => (
                  <PopoverDropdown
                    {...field}
                    enableSearch
                    placeholder="Select Technical Agent"
                    searchPlaceholder="Search Agent"
                    className="max-h-[300px] overflow-y-scroll"
                    valueStyle={{
                      width: "248px",
                    }}
                    formState={formState}
                    options={agents}
                    onSelect={(data) => {
                      setValue("agentName", data.value);
                      setValue("agentId", data.id);
                      clearErrors("agentName");
                      setSelectedAgent(data);
                    }}
                  />
                )}
              />
            </div>
          </div>

          <p className="font-poppins font-medium text-xs leading-none text-black mt-6">
            Assign Batteries
          </p>
          <p
            className={`mt-2 font-aileron font-normal text-xs leading-125 mb-6 ${
              hasLowChargeBattery ? "text-alert-red" : "text-dark-gray"
            }`}
          >
            {hasLowChargeBattery
              ? `${lowChargeCount}/${selected.length} selected ${
                  lowChargeCount === 1 ? "battery does" : "batteries do"
                } not have enough charge. Please select a charged battery if assigning ${
                  lowChargeCount === 1 ? "it" : "them"
                } for a trip.`
              : "Please select from the list of batteries at your hub station."}
          </p>

          <p
            className={`font-aileron font-semibold text-sm mt-6 ${
              selected.length !== +requestedBatteryCount
                ? "text-warning"
                : "text-primary"
            }`}
          >
            {selected.length}/{requestedBatteryCount} batteries assigned from
            the Smart Battery Container
          </p>

          <CustomInput
            intent="searchHome"
            prefix={
              <img src="/assets/icons/search.svg" alt="" className="mr-2" />
            }
            value={search}
            focus={focus}
            border={focus ? "success" : "none"}
            placeholder={focus ? null : "Search Battery No."}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            suffix={
              search.length > 0 ? (
                <CloseIcon
                  className="w-4 h-4 cursor-pointer stroke-dark-grey-text"
                  onClick={() => {
                    setFocus(false);
                    setSearch("");
                  }}
                />
              ) : null
            }
          />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center my-10">
              <Spin
                spinning={loading}
                indicator={<LoadingOutlined size="large" />}
              />
            </div>
          ) : (
            getFilteredBatteries().map((battery, index, batteriesList) => {
              const isChecked = selected.includes(battery.id);
              const isLast = index === batteriesList.length - 1;

              return (
                <div
                  key={battery.id}
                  className={`flex items-center mx-6 px-6 py-3 gap-x-12 ${
                    !isLast ? "border-b border-light-grey" : ""
                  } ${isChecked ? "bg-primary-20" : ""}`}
                >
                  <div className="flex gap-x-3">
                    <Checkbox
                      className="w-4 h-4"
                      checked={isChecked}
                      onChange={() => handleSelection(battery.id)}
                    />

                    <p className="w-[236px] font-aileron text-xs font-semibold">
                      {battery.registrationNo}
                    </p>
                  </div>

                  <div className="h-6.5 min-w-[32px] px-2 py-1 rounded bg-[#E6EDEE] flex items-center justify-center">
                    <p className="font-poppins font-medium text-xs leading-none text-[#085054]">
                      {battery.lockerId || "--"}
                    </p>
                  </div>

                  <BatteryPercentage
                    percentage={
                      battery.percentage >= 0 ? +battery.percentage : -1
                    }
                  />
                </div>
              );
            })
          )}
        </div>

        <div className="shrink-0 bg-background-white px-6 pb-6 pt-3">
          {disabled && (
            <div className="flex items-start gap-x-2 mb-3">
              <AlertIcon size={16} className="text-alert-red" />

              <p className="font-aileron text-xs font-medium text-alert-red">
                {disabled}
              </p>
            </div>
          )}

          <div className="flex items-center gap-x-3">
            <Button
              colors="grey"
              width="batterySwapStep3"
              onClick={handleCancel}
            >
              Close
            </Button>

            <button
              type="button"
              className="bg-primary hover:bg-primary-50 px-4 py-3 rounded-md disabled:cursor-not-allowed disabled:hover:bg-primary w-[250px]"
              onClick={handleConfirm}
            >
              <p className="text-background-white font-poppins text-xs font-medium capitalize">
                Confirm
              </p>
            </button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default AcceptBatteryRequestModal;
